import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, Loader2, Trash2, X, RotateCw, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';

const EMPTY = { name: '', category: '', amount: 0, paid_to: '', method: 'Bank', description: '', day_of_month: 1, active: true };

const RecurringPage = () => {
  const { authHeader } = useAuth();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: lst }, { data: cs }] = await Promise.all([
        axios.get(`${API}/recurring-expenses`, { headers: authHeader() }),
        axios.get(`${API}/expense-categories`, { headers: authHeader() }),
      ]);
      setList(lst || []); setCats(cs || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    if (!editing.name || !editing.category || !editing.amount) {
      setErr('Name, category and amount required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      await axios.post(`${API}/recurring-expenses`, editing, { headers: authHeader() });
      setEditing(null);
      await fetchAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this recurring template? Existing generated expenses are kept.')) return;
    await axios.delete(`${API}/recurring-expenses/${id}`, { headers: authHeader() });
    await fetchAll();
  };

  const runNow = async () => {
    try {
      const { data } = await axios.post(`${API}/recurring-expenses/run-now`, {}, { headers: authHeader() });
      alert(`Generated ${data.created} expense entries for ${data.month}.`);
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="recurring-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/expenses" className="p-1.5 rounded hover:bg-slate-200 text-slate-600"><ArrowLeft size={16} /></Link>
          <RotateCw className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Recurring Expenses</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={runNow} className="text-sm bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="run-now-btn">
            <RefreshCw size={14} /> Run Now
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="new-recurring-btn">
            <Plus size={14} /> New Template
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500">Templates auto-create an expense entry each month on the chosen day. Use "Run Now" to manually trigger for the current month (idempotent — each template runs at most once per month).</p>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No recurring templates. Add one to auto-generate fixed monthly expenses (rent, internet, salary, etc.).</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Name</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Category</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Day</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Amount</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Status</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Last Run</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-900 font-medium">{t.name}</td>
                    <td className="px-3 py-2 text-slate-700">{t.category}</td>
                    <td className="px-3 py-2 text-slate-600">{t.day_of_month}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtINR(t.amount)}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${t.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{t.active ? 'Active' : 'Paused'}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{t.last_generated_for || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => del(t.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`del-recurring-${t.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">New Recurring Template</h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">{err}</div>}
              <input type="text" placeholder="Name (e.g. Workshop Rent)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls}>
                  <option value="">Category…</option>
                  {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <input type="number" min={0} placeholder="Amount" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: Number(e.target.value || 0) })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Day of month (1-28)</label>
                  <input type="number" min={1} max={28} value={editing.day_of_month} onChange={(e) => setEditing({ ...editing, day_of_month: Math.max(1, Math.min(28, Number(e.target.value || 1))) })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Method</label>
                  <input type="text" value={editing.method} onChange={(e) => setEditing({ ...editing, method: e.target.value })} className={inputCls} />
                </div>
              </div>
              <input type="text" placeholder="Paid to (optional)" value={editing.paid_to} onChange={(e) => setEditing({ ...editing, paid_to: e.target.value })} className={inputCls} />
              <textarea rows={2} placeholder="Description (optional)" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} />
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={save} disabled={saving} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringPage;
